import React, { lazy, Suspense } from "react";
import { Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import { useAuth } from "../context/connectionContext";
import ResponsiveAppBar from "../layout/ResponsiveAppBar";
import { CssBaseline, Stack } from "@mui/material";
import ErrorPage from "../errorPage";
import ProtectedRoute from "./protectedRoute";
import ResponsiveLayout from "../layout/appBar/ResponsiveLayout";
import LoadingPage from "../components/LoadingPage";
import { ErrorBoundary } from "../components/GeneralErrorBoundry";
import RouteLoadingFallback from "../components/RouteLoadingFallback";

const AssetPage = lazy(() => import("./assetPage"));
const Portfolio = lazy(() => import("./portfolio"));
const SafesPage = lazy(() => import("./safesPage"));
const Create = lazy(() => import("./create"));
const CreateProfile = lazy(() => import("./create/CreateProfile"));
const HomePage = lazy(() => import("./homePage"));
const HelpersPage = lazy(() => import("./helpersPage"));
const TrustorsPage = lazy(() => import("./trustorsPage"));
const ManageSafeLayout = lazy(() => import("./manage_safe/ManageSafeLayout"));
const ManageSafeDefault = lazy(() => import("./manage_safe/ManageSafeDefault"));
const ActionsPage = lazy(() => import("./actionsPage"));
const ManageSafeUsersPage = lazy(() => import("./manageSafeUsers"));
const ManageSafePoliciesPage = lazy(() => import("./manageSafePolicies"));
const AddressBookPage = lazy(() => import("./manage_safe/address_book/AddressBookPage"));

const AppRoutes: React.FC = () => {
  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
            }}
          >
            <ResponsiveAppBar />
            <ResponsiveLayout>
              <Outlet />
            </ResponsiveLayout>
          </div>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            element: (
              <ErrorBoundary>
                <ProtectedRoute />
              </ErrorBoundary>
            ),
            path: "/",
            children: [
              {
                path: "",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Home..." />}>
                    <HomePage />
                  </Suspense>
                ),
              },
              {
                path: "safes",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Safes..." />}>
                    <SafesPage />
                  </Suspense>
                ),
              },
              {
                path: "portfolio/:safeAddress",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Portfolio..." />}>
                    <Portfolio />
                  </Suspense>
                ),
              },
              {
                path: "portfolio/:safeAddress/:assetSymbol/:chainName?/:contractAddress?",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Asset..." />}>
                    <AssetPage />
                  </Suspense>
                ),
              },
              {
                path: "create",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Create..." />}>
                    <Create />
                  </Suspense>
                ),
                children: [
                  {
                    path: "profile/:account",
                    element: (
                      <Suspense fallback={<RouteLoadingFallback label="Loading Profile..." />}>
                        <CreateProfile />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "helpers",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Helpers..." />}>
                    <HelpersPage />
                  </Suspense>
                ),
              },
              {
                path: "trustors",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Trustors..." />}>
                    <TrustorsPage />
                  </Suspense>
                ),
              },
              {
                path: "manage-safe/:safeAddress",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Manage Safe..." />}>
                    <ManageSafeLayout />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<RouteLoadingFallback label="Loading..." />}>
                        <ManageSafeDefault />
                      </Suspense>
                    ),
                  },
                  {
                    path: "users",
                    element: (
                      <Suspense fallback={<RouteLoadingFallback label="Loading Users..." />}>
                        <ManageSafeUsersPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "policies",
                    element: (
                      <Suspense fallback={<RouteLoadingFallback label="Loading Policies..." />}>
                        <ManageSafePoliciesPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "address-book",
                    element: (
                      <Suspense fallback={<RouteLoadingFallback label="Loading Address Book..." />}>
                        <AddressBookPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "actions/:safeAddress?",
                element: (
                  <Suspense fallback={<RouteLoadingFallback label="Loading Actions..." />}>
                    <ActionsPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
      },
    }
  );

  const { providerClient } = useAuth();

  return (
    <Stack spacing={2} sx={{ flexGrow: 1 }}>
      <CssBaseline />
      {providerClient ? <RouterProvider router={router} /> : <LoadingPage />}
    </Stack>
  );
};

export default AppRoutes;
